import { axiosInstance } from "../client";
import { CollectionResponse } from "@rumble-it/common-lib/types/api/CollectionResponse.interface";
import {
  ChatChannel,
  ChatConversation,
  ChatConversationDetail,
  ChatConversationStatus,
} from "@rumble-it/common-lib/types/models/ChatConversation.interface";
import {
  ChatAvailability,
  ChatMessage,
  ChatMessageDraftInput,
  ChatTemplate,
} from "@rumble-it/common-lib/types/models/ChatMessage.interface";

export interface ChatConversationFilters {
  channel?: ChatChannel;
  status?: ChatConversationStatus;
  dossierId?: string;
  archived?: boolean;
  unhandled?: boolean;
  page?: number;
}

/**
 * Query strings can only carry strings, and the API reads a present boolean
 * filter as `true` regardless of its value. So only forward boolean filters
 * when they are `true`, and drop empty values, to avoid `archived=false`
 * being interpreted as `archived=true`.
 */
const toQueryParams = (
  filters: ChatConversationFilters,
): Record<string, string | number> => {
  const params: Record<string, string | number> = {};

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === false) continue;
    params[key] = value === true ? "true" : value;
  }

  return params;
};

export const fetchChatConversations = async (
  filters: ChatConversationFilters = {},
): Promise<CollectionResponse<ChatConversation>> =>
  axiosInstance
    .get(`/admin/chat/conversations`, { params: toQueryParams(filters) })
    .then((response) => response.data);

export const fetchChatConversationById = async (
  conversationId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .get(`/admin/chat/conversations/${conversationId}`)
    .then((response) => response.data);

export const fetchChatUnhandledCount = async (): Promise<number> =>
  axiosInstance
    .get(`/admin/chat/conversations/unhandled-count`)
    .then((response) => response.data.count);

export const markChatConversationHandled = async (
  conversationId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/mark-handled`)
    .then((response) => response.data);

export const archiveChatConversation = async (
  conversationId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/archive`)
    .then((response) => response.data);

export const unarchiveChatConversation = async (
  conversationId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/unarchive`)
    .then((response) => response.data);

export const markChatConversationUnread = async (
  conversationId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/mark-unread`)
    .then((response) => response.data);

export const draftChatMessage = async (
  dossierId: string,
  data: ChatMessageDraftInput,
): Promise<ChatMessage> =>
  axiosInstance
    .post(`/admin/dossiers/${dossierId}/chat/messages`, data)
    .then((response) => response.data);

export const draftChatMessageOnConversation = async (
  conversationId: string,
  data: ChatMessageDraftInput,
): Promise<ChatMessage> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/messages`, data)
    .then((response) => response.data);

export const sendChatMessage = async (
  messageId: string,
): Promise<ChatMessage> =>
  axiosInstance
    .post(`/admin/chat/messages/${messageId}/send`)
    .then((response) => response.data);

export const moveChatMessage = async (
  messageId: string,
  dossierId: string,
): Promise<null> =>
  axiosInstance
    .post(`/admin/chat/messages/${messageId}/move/${dossierId}`)
    .then((response) => response.data);

export const moveChatConversation = async (
  conversationId: string,
  dossierId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/move/${dossierId}`)
    .then((response) => response.data);

export const unlinkChatConversation = async (
  conversationId: string,
): Promise<ChatConversationDetail> =>
  axiosInstance
    .post(`/admin/chat/conversations/${conversationId}/unlink`)
    .then((response) => response.data);

export const fetchChatTemplates = async (): Promise<ChatTemplate[]> =>
  axiosInstance.get(`/admin/chat/templates`).then((response) => response.data);

export const fetchChatAvailability = async (
  dossierId: string,
): Promise<ChatAvailability> =>
  axiosInstance
    .get(`/admin/dossiers/${dossierId}/chat/availability`)
    .then((response) => response.data);

export const fetchChatMediaBlob = async (mediaId: string): Promise<Blob> =>
  axiosInstance
    .get(`/media/${mediaId}/as-stream`, { responseType: "blob" })
    .then((response) => response.data);
